import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getTeamSettings,
  patchTeamSettings,
  type ApiError,
} from "@/lib/api";
import { invalidateScope, useScopedKey } from "@/lib/queryClient";
import { useScopeStore } from "@/stores/scope";
import type { TeamSettings, TeamSettingsResponse } from "@/lib/types";

/**
 * Coach > Settings data hooks (M6b).
 *
 * `useTeamSettings` reads the active team's settings + the closed
 * schema registry the page renders its controls from. Scope-keyed
 * + gated on `teamId` like the other coach queries, so a team /
 * season scope change refetches the right team's settings.
 *
 * `useTeamSettingsMutation` wraps the PATCH. The backend gates the
 * write on the `team_settings:manage` capability — a plain coach's
 * PATCH 403s — and `invalidateScope`s on success so the page
 * re-renders with the saved state. The page itself reads
 * `can_edit` from the GET payload to disable the save UI before
 * the request, so the 403 is a defense-in-depth backstop.
 */

export function useTeamSettings() {
  const teamId = useScopeStore((s) => s.teamId);
  return useQuery<TeamSettingsResponse>({
    queryKey: useScopedKey("team-settings"),
    queryFn: getTeamSettings,
    enabled: !!teamId,
  });
}

export type TeamSettingsMutation = {
  save: (settings: TeamSettings) => Promise<TeamSettingsResponse>;
  isPending: boolean;
  error: ApiError | null;
};

export function useTeamSettingsMutation(): TeamSettingsMutation {
  const patch = useMutation({
    mutationFn: patchTeamSettings,
    onSuccess: () => invalidateScope(),
  });
  return {
    save: (settings) => patch.mutateAsync(settings),
    isPending: patch.isPending,
    error: (patch.error as ApiError | null) ?? null,
  };
}
